/**
 * The price book (CAT-1) — everything Keystone prices from.
 *
 * Items carry a cost; sell prices come from cost at the quote's margin unless
 * an item is given its own margin or a fixed price. Kits are the thing that
 * makes a site walk fast: a driver quantity ("how many cameras") and a
 * formula per line, so changing the count moves the cable, the labour and
 * the hire with it.
 */
import { useEffect, useRef, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { ApiError, api } from "../lib/api";
import { OFFICE, useAuth } from "../lib/auth";
import { money, percent, qty as showQty, titleCase } from "../lib/format";
import { itemConflict } from "../lib/pricebook";
import {
  MAX_MARGIN_PCT,
  MAX_MARKUP_PCT,
  type PriceMode,
  bpToPctText,
  marginPreview,
  markupPreview,
  markupTag,
  pctToBp,
  priceFields,
  priceModeOf,
} from "../lib/pricemode";
import { sellsBelowCost } from "../lib/servicerates";
import { Card, Chip, Empty, ErrorState, Loading, PageHeader } from "../components/ui";
import type { CatalogueItemOut, ItemIn, ItemKind, KitDetailOut, KitOut } from "../lib/types";

const KINDS: ItemKind[] = ["part", "labour", "hire", "subcontract", "fee"];
type Tab = "items" | "kits" | "suppliers";

const cents = (text: string): number => Math.max(0, Math.round((Number(text.replace(/[$,\s]/g, "")) || 0) * 100));

export default function Catalogue() {
  const { can } = useAuth();
  const canEdit = can(...OFFICE);
  const [tab, setTab] = useState<Tab>("items");

  return (
    <div className="page">
      <PageHeader title="Price book">
        What things cost you. Quotes and jobs price from here, so this is where a supplier's increase lands once.
      </PageHeader>

      <div className="segmented" role="group" aria-label="Price book">
        {(["items", "kits", "suppliers"] as Tab[]).map((t) => (
          <button key={t} type="button" className={tab === t ? "active" : ""} onClick={() => setTab(t)}>
            {t === "items" ? "Items" : t === "kits" ? "Kits" : "Suppliers"}
          </button>
        ))}
      </div>

      {tab === "items" && <Items canEdit={canEdit} />}
      {tab === "kits" && <Kits canEdit={canEdit} />}
      {tab === "suppliers" && <Suppliers canEdit={canEdit} />}
    </div>
  );
}

/* ------------------------------------------------------------------- items */

function Items({ canEdit }: { canEdit: boolean }) {
  const queryClient = useQueryClient();
  const [search, setSearch] = useState("");
  const [kind, setKind] = useState<"" | ItemKind>("");
  const [retired, setRetired] = useState(false);
  const [adding, setAdding] = useState(false);
  const [editing, setEditing] = useState<string | null>(null);
  const [problem, setProblem] = useState<string | null>(null);

  const list = useQuery({
    queryKey: ["catalogue-items", search.trim(), kind, retired],
    queryFn: () => api.catalogue.items(search.trim() || undefined, { kind: kind || undefined, include_retired: retired }),
    placeholderData: (p) => p,
  });
  const refresh = () => {
    void queryClient.invalidateQueries({ queryKey: ["catalogue-items"] });
    void queryClient.invalidateQueries({ queryKey: ["kits"] });
  };
  const fail = (err: unknown) => setProblem(err instanceof ApiError ? err.message : "Couldn't save that.");
  const retire = useMutation({
    mutationFn: (id: string) => api.catalogue.retireItem(id),
    onSuccess: refresh,
    onError: fail,
  });

  return (
    <>
      <div className="toolbar">
        <label className="search">
          <input type="search" value={search} placeholder="Description or SKU" onChange={(e) => setSearch(e.target.value)} />
        </label>
        <select value={kind} onChange={(e) => setKind(e.target.value as "" | ItemKind)} aria-label="Kind">
          <option value="">Everything</option>
          {KINDS.map((k) => (
            <option key={k} value={k}>
              {titleCase(k)}
            </option>
          ))}
        </select>
        <label className="tiny" style={{ display: "flex", gap: 6, alignItems: "center" }}>
          <input type="checkbox" checked={retired} onChange={(e) => setRetired(e.target.checked)} />
          Show retired
        </label>
        {canEdit && (
          <button className="btn btn-primary" type="button" onClick={() => setAdding((v) => !v)}>
            Add an item
          </button>
        )}
      </div>

      {problem && (
        <p className="notice notice-crit" role="alert">
          {problem}
        </p>
      )}
      {adding && <ItemForm onCancel={() => setAdding(false)} onSaved={() => { setAdding(false); refresh(); }} onError={fail} />}

      {list.isPending ? (
        <Loading what="Loading the price book" />
      ) : list.error ? (
        <ErrorState error={list.error} onRetry={() => void list.refetch()} />
      ) : list.data.length === 0 ? (
        <Card>
          <Empty title="Nothing here yet">Add the parts, labour rates and hire you quote from.</Empty>
        </Card>
      ) : (
        <Card padded={false}>
          <div className="table-scroll">
            <table>
              <thead>
                <tr>
                  <th>Item</th>
                  <th className="phone-hide">Kind</th>
                  <th className="right">Cost</th>
                  <th className="right phone-hide">Sell</th>
                  {canEdit && <th />}
                </tr>
              </thead>
              <tbody>
                {list.data.map((item) =>
                  editing === item.id ? (
                    <tr key={item.id}>
                      <td colSpan={canEdit ? 5 : 4}>
                        <ItemForm item={item} onCancel={() => setEditing(null)} onSaved={() => { setEditing(null); refresh(); }} onStale={refresh} onError={fail} />
                      </td>
                    </tr>
                  ) : (
                    <tr key={item.id} className={item.active === false ? "muted" : undefined}>
                      <td>
                        {item.description}
                        {item.sku && <div className="tiny muted mono">{item.sku}</div>}
                        <span className="tiny">
                          {item.is_serialised && <Chip tone="accent">serialised</Chip>}
                          {item.active === false && <Chip tone="neutral">retired</Chip>}
                          {item.cost_cents === 0 && <Chip tone="warn">no cost</Chip>}
                          {item.below_cost && <Chip tone="warn">below cost</Chip>}
                          {item.markup_bp !== null && item.markup_bp !== undefined && (
                            <Chip tone="info">{markupTag(item.markup_bp)}</Chip>
                          )}
                        </span>
                      </td>
                      <td className="phone-hide">
                        {titleCase(item.kind)} <span className="tiny muted">/{item.uom}</span>
                      </td>
                      <td className="right mono">{money(item.cost_cents)}</td>
                      <td className="right mono phone-hide">
                        {item.sell_cents ? money(item.sell_cents) : <span className="muted tiny">from margin</span>}
                      </td>
                      {canEdit && (
                        <td className="right">
                          <button className="linkish tiny" type="button" onClick={() => setEditing(item.id)}>
                            Edit
                          </button>{" "}
                          <button className="linkish tiny" type="button" onClick={() => retire.mutate(item.id)}>
                            {item.active === false ? "Use again" : "Retire"}
                          </button>
                        </td>
                      )}
                    </tr>
                  ),
                )}
              </tbody>
            </table>
          </div>
        </Card>
      )}
    </>
  );
}

function ItemForm({
  item,
  onSaved,
  onCancel,
  onError,
  onStale,
}: {
  item?: CatalogueItemOut;
  onSaved: () => void;
  onCancel: () => void;
  onError: (err: unknown) => void;
  /** Someone else saved first; the list behind this form is out of date. */
  onStale?: () => void;
}) {
  // The item as last loaded. Its `rev` goes back with every save; a refusal
  // replaces it with what is there now, so the next save is against that.
  const [base, setBase] = useState(item);
  const [stale, setStale] = useState<string | null>(null);
  // On a phone the form is long and Save is at the bottom; the warning above
  // it was 500px off screen, so a refused save looked like nothing happening.
  const staleRef = useRef<HTMLParagraphElement | null>(null);
  useEffect(() => {
    if (stale) staleRef.current?.scrollIntoView({ block: "center", behavior: "smooth" });
  }, [stale]);
  const suppliers = useQuery({ queryKey: ["suppliers"], queryFn: () => api.catalogue.suppliers() });
  const [form, setForm] = useState({
    description: item?.description ?? "",
    sku: item?.sku ?? "",
    kind: (item?.kind ?? "part") as ItemKind,
    uom: item?.uom ?? "ea",
    cost: ((item?.cost_cents ?? 0) / 100).toFixed(2),
    sell: item?.sell_cents ? (item.sell_cents / 100).toFixed(2) : "",
    mode: priceModeOf(item ?? {}) as PriceMode,
    markup: bpToPctText(item?.markup_bp),
    margin: bpToPctText(item?.margin_bp),
    supplier_id: item?.supplier_id ?? "",
    is_serialised: item?.is_serialised ?? false,
    warranty_months: item?.warranty_months ? String(item.warranty_months) : "",
  });
  const body = (): ItemIn => ({
    description: form.description.trim(),
    sku: form.sku.trim() || null,
    kind: form.kind,
    uom: form.uom.trim() || "ea",
    cost_cents: cents(form.cost),
    // Exactly one of sell / margin / markup is sent; the others are cleared,
    // because the server refuses a markup alongside a sell or margin (F3).
    ...priceFields(form.mode, { sellCents: form.sell.trim() ? cents(form.sell) : 0, marginText: form.margin, markupText: form.markup }),
    supplier_id: form.supplier_id || null,
    is_serialised: form.is_serialised,
    warranty_months: form.warranty_months ? Number(form.warranty_months) : null,
  });
  const markup = pctToBp(form.markup, MAX_MARKUP_PCT);
  const margin = pctToBp(form.margin, MAX_MARGIN_PCT);
  const pricingOk = form.mode === "markup" ? markup.valid : form.mode === "margin" ? margin.valid : true;
  const save = useMutation({
    mutationFn: () =>
      base
        ? api.catalogue.updateItem(base.id, { ...body(), expected_rev: base.rev })
        : api.catalogue.addItem(body()),
    onSuccess: onSaved,
    onError: (err) => {
      const conflict = itemConflict(err);
      if (!conflict) {
        onError(err);
        return;
      }
      // What you typed stays on screen; what is saved now is named so the
      // next Save is a decision, not an accident.
      const now = conflict.current;
      setBase(now);
      setStale(
        `${conflict.message} It is now: cost ${money(now.cost_cents)}, ` +
          `${now.sell_cents ? `sell ${money(now.sell_cents)}` : "sell from margin"}. ` +
          "Your changes are still in the form; Save again to overwrite theirs.",
      );
      onStale?.();
    },
  });

  // PRICE-2: a fixed price under cost is allowed, but never by accident.
  const confirmAndSave = () => {
    const sell = cents(form.sell);
    const cost = cents(form.cost);
    if (
      sellsBelowCost(form.mode, form.sell, sell, cost) &&
      !window.confirm(`This sells below cost: ${money(sell)} sell on ${money(cost)} cost. Save anyway?`)
    ) {
      return;
    }
    save.mutate();
  };

  return (
    <div className="book-form" style={{ display: "block" }}>
      {stale && (
        <p ref={staleRef} className="notice notice-warn" role="alert">
          {stale}
        </p>
      )}
      <div className="form-grid">
        <label className="field" style={{ gridColumn: "1 / -1" }}>
          <span>Description — what prints on the quote</span>
          <input value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} autoFocus />
        </label>
        <label className="field">
          <span>Kind</span>
          <select value={form.kind} onChange={(e) => setForm({ ...form, kind: e.target.value as ItemKind })}>
            {KINDS.map((k) => (
              <option key={k} value={k}>
                {titleCase(k)}
              </option>
            ))}
          </select>
        </label>
        <label className="field">
          <span>Unit</span>
          <input value={form.uom} onChange={(e) => setForm({ ...form, uom: e.target.value })} placeholder="ea, hr, m, box" />
        </label>
        <label className="field">
          <span>Cost ex GST</span>
          <input inputMode="decimal" value={form.cost} onChange={(e) => setForm({ ...form, cost: e.target.value })} />
        </label>
        <label className="field">
          <span>Priced by</span>
          <select value={form.mode} onChange={(e) => setForm({ ...form, mode: e.target.value as PriceMode })}>
            <option value="markup">Markup on cost</option>
            <option value="margin">Margin</option>
            <option value="fixed">Fixed sell price</option>
          </select>
        </label>
        {form.mode === "markup" && (
          <label className="field">
            <span>Markup %</span>
            <input inputMode="decimal" value={form.markup} onChange={(e) => setForm({ ...form, markup: e.target.value })} placeholder="40" />
          </label>
        )}
        {form.mode === "margin" && (
          <label className="field">
            <span>Margin % (optional)</span>
            <input inputMode="decimal" value={form.margin} onChange={(e) => setForm({ ...form, margin: e.target.value })} placeholder="business default" />
          </label>
        )}
        {form.mode === "fixed" && (
          <label className="field">
            <span>Fixed sell price</span>
            <input inputMode="decimal" value={form.sell} onChange={(e) => setForm({ ...form, sell: e.target.value })} placeholder="$" />
          </label>
        )}
        <label className="field">
          <span>SKU</span>
          <input value={form.sku} onChange={(e) => setForm({ ...form, sku: e.target.value })} />
        </label>
        <label className="field">
          <span>Supplier</span>
          <select value={form.supplier_id} onChange={(e) => setForm({ ...form, supplier_id: e.target.value })}>
            <option value="">None</option>
            {(suppliers.data ?? []).map((s) => (
              <option key={s.id} value={s.id}>
                {s.name}
              </option>
            ))}
          </select>
        </label>
        <label className="field">
          <span>Warranty (months)</span>
          <input inputMode="numeric" value={form.warranty_months} onChange={(e) => setForm({ ...form, warranty_months: e.target.value })} />
        </label>
      </div>
      <label className="tiny" style={{ display: "flex", gap: 6, alignItems: "center", marginTop: 8 }}>
        <input type="checkbox" checked={form.is_serialised} onChange={(e) => setForm({ ...form, is_serialised: e.target.checked })} />
        Serialised — the tech scans one of these at install, and it becomes an asset with a warranty date
      </label>
      {form.mode === "markup" && markup.valid && markup.bp !== null && (
        <p className="tiny" style={{ marginTop: 6 }} data-testid="markup-preview">
          Sells at {money(markupPreview(cents(form.cost), markup.bp).sellCents)} (margin{" "}
          {percent(markupPreview(cents(form.cost), markup.bp).marginBp)}). It follows the cost when the cost changes.
        </p>
      )}
      {form.mode === "margin" && margin.valid && margin.bp !== null && (
        <p className="tiny" style={{ marginTop: 6 }} data-testid="margin-preview">
          Sells at {money(marginPreview(cents(form.cost), margin.bp))}.
        </p>
      )}
      {((form.mode === "markup" && !markup.valid) || (form.mode === "margin" && !margin.valid)) && (
        <p className="notice notice-warn" role="status" style={{ marginTop: 8 }}>
          {form.mode === "markup"
            ? `Markup needs to be a percentage from 0 to ${MAX_MARKUP_PCT}.`
            : `Margin needs to be a percentage below 99.`}
        </p>
      )}
      {cents(form.cost) === 0 && (form.mode !== "fixed" || !form.sell.trim()) && (
        <p className="tiny" style={{ marginTop: 6 }}>
          <Chip tone="warn">no cost</Chip> It will charge nothing on a quote. Give it a cost — a notional one is fine.
        </p>
      )}
      <div className="btn-row" style={{ marginTop: 8 }}>
        <button className="btn btn-primary btn-sm" type="button" disabled={!form.description.trim() || !pricingOk || save.isPending} onClick={confirmAndSave}>
          {save.isPending ? "Saving…" : item ? "Save" : "Add item"}
        </button>
        <button className="btn btn-sm" type="button" onClick={onCancel}>
          Cancel
        </button>
      </div>
    </div>
  );
}

/* -------------------------------------------------------------------- kits */

function Kits({ canEdit }: { canEdit: boolean }) {
  const queryClient = useQueryClient();
  const [open, setOpen] = useState<string | null>(null);
  const [adding, setAdding] = useState(false);
  const [problem, setProblem] = useState<string | null>(null);
  const kits = useQuery({ queryKey: ["kits"], queryFn: () => api.catalogue.kits() });
  const fail = (err: unknown) => setProblem(err instanceof ApiError ? err.message : "Couldn't save that.");
  const refresh = () => void queryClient.invalidateQueries({ queryKey: ["kits"] });

  return (
    <>
      {problem && (
        <p className="notice notice-crit" role="alert">
          {problem}
        </p>
      )}
      {canEdit && (
        <div className="btn-row" style={{ marginBottom: 12 }}>
          <button className="btn btn-primary" type="button" onClick={() => setAdding((v) => !v)}>
            New kit
          </button>
        </div>
      )}
      {adding && <NewKit onCancel={() => setAdding(false)} onAdded={(k) => { setAdding(false); refresh(); setOpen(k.id); }} onError={fail} />}

      {kits.isPending ? (
        <Loading what="Loading kits" />
      ) : (kits.data ?? []).length === 0 ? (
        <Card>
          <Empty title="No kits yet">
            A kit is a job type priced once — "external turret CCTV" with its cable, labour and hire per camera.
          </Empty>
        </Card>
      ) : (
        (kits.data ?? []).map((kit) => (
          <KitCard key={kit.id} kit={kit} canEdit={canEdit} expanded={open === kit.id} onToggle={() => setOpen(open === kit.id ? null : kit.id)} onChanged={refresh} onError={fail} />
        ))
      )}
    </>
  );
}

function NewKit({ onAdded, onCancel, onError }: { onAdded: (k: KitDetailOut) => void; onCancel: () => void; onError: (err: unknown) => void }) {
  const [form, setForm] = useState({ code: "", name: "", driver_label: "cameras", default_driver_qty: "1", category: "" });
  const add = useMutation({
    mutationFn: () =>
      api.catalogue.addKit({
        code: form.code.trim(),
        name: form.name.trim(),
        driver_label: form.driver_label.trim() || "quantity",
        default_driver_qty: Number(form.default_driver_qty) || 1,
        category: form.category.trim() || null,
      }),
    onSuccess: onAdded,
    onError,
  });
  return (
    <Card title="New kit" subtitle="Priced per driver unit — how many cameras, doors, sites">
      <div className="form-grid">
        <label className="field">
          <span>Code</span>
          <input value={form.code} onChange={(e) => setForm({ ...form, code: e.target.value })} placeholder="CCTV-TURRET-EXT" />
        </label>
        <label className="field">
          <span>Name</span>
          <input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="External turret CCTV" />
        </label>
        <label className="field">
          <span>Driver — what the tech is asked</span>
          <input value={form.driver_label} onChange={(e) => setForm({ ...form, driver_label: e.target.value })} />
        </label>
        <label className="field">
          <span>Usual quantity</span>
          <input inputMode="decimal" value={form.default_driver_qty} onChange={(e) => setForm({ ...form, default_driver_qty: e.target.value })} />
        </label>
        <label className="field">
          <span>Category</span>
          <input value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} placeholder="cctv, alarm, access" />
        </label>
      </div>
      <div className="btn-row" style={{ marginTop: 10 }}>
        <button className="btn btn-primary" type="button" disabled={!form.code.trim() || !form.name.trim() || add.isPending} onClick={() => add.mutate()}>
          {add.isPending ? "Creating…" : "Create kit"}
        </button>
        <button className="btn" type="button" onClick={onCancel}>
          Cancel
        </button>
      </div>
    </Card>
  );
}

function KitCard({
  kit,
  canEdit,
  expanded,
  onToggle,
  onChanged,
  onError,
}: {
  kit: KitOut;
  canEdit: boolean;
  expanded: boolean;
  onToggle: () => void;
  onChanged: () => void;
  onError: (err: unknown) => void;
}) {
  const queryClient = useQueryClient();
  const detail = useQuery({ queryKey: ["kit", kit.id], queryFn: () => api.catalogue.kit(kit.id), enabled: expanded });
  const [driver, setDriver] = useState(String(kit.default_driver_qty));
  const preview = useQuery({
    queryKey: ["kit-preview", kit.id, driver],
    queryFn: () => api.catalogue.previewKit(kit.id, Number(driver) || 1, 4200),
    enabled: expanded && Number(driver) > 0,
  });
  const adopt = (next: KitDetailOut) => {
    queryClient.setQueryData(["kit", kit.id], next);
    void queryClient.invalidateQueries({ queryKey: ["kit-preview", kit.id] });
    onChanged();
  };
  const remove = useMutation({
    mutationFn: () => api.catalogue.removeKit(kit.id),
    onSuccess: () => {
      onChanged();
      onToggle();
    },
    onError,
  });

  return (
    <Card
      title={kit.name}
      subtitle={`${kit.code} · per ${kit.driver_label}`}
      actions={
        <span className="btn-row">
          <button className="linkish tiny" type="button" onClick={onToggle}>
            {expanded ? "Close" : "Open"}
          </button>
          {canEdit && expanded && (
            <button
              className="linkish tiny"
              type="button"
              onClick={() => {
                if (window.confirm(`Retire ${kit.name}? Quotes already built from it keep their lines.`)) remove.mutate();
              }}
            >
              Retire
            </button>
          )}
        </span>
      }
    >
      {expanded &&
        (detail.isPending ? (
          <Loading what="Loading the kit" />
        ) : detail.data ? (
          <>
            <KitLines kit={detail.data} canEdit={canEdit} onChanged={adopt} onError={onError} />
            <div className="btn-row" style={{ marginTop: 12, alignItems: "center" }}>
              <label className="field field-inline">
                <span>Preview at</span>
                <input inputMode="decimal" value={driver} onChange={(e) => setDriver(e.target.value)} style={{ width: 70 }} />
              </label>
              <span className="tiny muted">{kit.driver_label}</span>
            </div>
            {preview.data && (
              <div className="table-scroll" style={{ marginTop: 6 }}>
                <table className="tiny">
                  <thead>
                    <tr>
                      <th>Line</th>
                      <th>Qty</th>
                      <th className="right">Cost</th>
                      <th className="right">Charge at 42%</th>
                    </tr>
                  </thead>
                  <tbody>
                    {preview.data.lines.map((line, i) => (
                      <tr key={`${line.description}-${i}`}>
                        <td>{line.description}</td>
                        <td className="mono">
                          {showQty(line.qty)} {line.uom}
                        </td>
                        <td className="right mono muted">{money(line.cost_cents)}</td>
                        <td className="right mono">{money(line.sell_cents)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </>
        ) : null)}
    </Card>
  );
}

function KitLines({
  kit,
  canEdit,
  onChanged,
  onError,
}: {
  kit: KitDetailOut;
  canEdit: boolean;
  onChanged: (next: KitDetailOut) => void;
  onError: (err: unknown) => void;
}) {
  const [search, setSearch] = useState("");
  const items = useQuery({
    queryKey: ["catalogue-items", search.trim(), "", false],
    queryFn: () => api.catalogue.items(search.trim() || undefined),
    enabled: canEdit && search.trim().length > 0,
  });
  const add = useMutation({
    mutationFn: (itemId: string) => api.catalogue.addKitLine(kit.id, { catalogue_item_id: itemId, qty_formula: "n" }),
    onSuccess: (next) => {
      setSearch("");
      onChanged(next);
    },
    onError,
  });
  const update = useMutation({
    mutationFn: ({ lineId, body }: { lineId: string; body: { qty_formula?: string; is_optional?: boolean } }) =>
      api.catalogue.updateKitLine(kit.id, lineId, body),
    onSuccess: onChanged,
    onError,
  });
  const remove = useMutation({ mutationFn: (lineId: string) => api.catalogue.removeKitLine(kit.id, lineId), onSuccess: onChanged, onError });

  return (
    <>
      <div className="table-scroll">
        <table>
          <thead>
            <tr>
              <th>Line</th>
              <th>How many</th>
              <th className="right phone-hide">Unit cost</th>
              {canEdit && <th />}
            </tr>
          </thead>
          <tbody>
            {kit.lines.map((line) => (
              <tr key={line.id}>
                <td>
                  {line.description}
                  {line.is_optional && (
                    <>
                      {" "}
                      <Chip tone="warn">optional</Chip>
                    </>
                  )}
                  {line.note && <div className="tiny muted">{line.note}</div>}
                </td>
                <td>
                  {canEdit ? (
                    <FormulaInput value={line.qty_formula} onSave={(qty_formula) => update.mutate({ lineId: line.id, body: { qty_formula } })} />
                  ) : (
                    <span className="mono tiny">{line.qty_formula}</span>
                  )}
                </td>
                <td className="right mono phone-hide">{money(line.unit_cost_cents)}</td>
                {canEdit && (
                  <td className="right">
                    <button className="linkish tiny" type="button" onClick={() => update.mutate({ lineId: line.id, body: { is_optional: !line.is_optional } })}>
                      {line.is_optional ? "Always" : "Optional"}
                    </button>{" "}
                    <button className="linkish tiny" type="button" onClick={() => remove.mutate(line.id)}>
                      Remove
                    </button>
                  </td>
                )}
              </tr>
            ))}
            {kit.lines.length === 0 && (
              <tr>
                <td colSpan={canEdit ? 4 : 3} className="tiny muted">
                  No lines yet.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {canEdit && (
        <div style={{ marginTop: 10 }}>
          <label className="field">
            <span>Add a line from the price book</span>
            <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search items…" />
          </label>
          {search.trim() && (
            <div className="stack" style={{ marginTop: 6, maxHeight: 200, overflowY: "auto" }}>
              {(items.data ?? []).slice(0, 8).map((item) => (
                <button key={item.id} type="button" className="btn btn-sm" style={{ justifyContent: "space-between" }} onClick={() => add.mutate(item.id)}>
                  <span>{item.description}</span>
                  <span className="tiny muted mono">
                    {money(item.cost_cents)}/{item.uom}
                  </span>
                </button>
              ))}
            </div>
          )}
          <p className="tiny muted" style={{ marginTop: 6 }}>
            "How many" is per <b>{kit.driver_label}</b>, written as a sum in <code>n</code>: <code>n</code> for one each,{" "}
            <code>n * 0.175</code> for a box of cable per six, <code>ceil(n / 4)</code> for a hire day per four,{" "}
            <code>2</code> for a flat two.
          </p>
        </div>
      )}
    </>
  );
}

function FormulaInput({ value, onSave }: { value: string; onSave: (next: string) => void }) {
  const [text, setText] = useState(value);
  return (
    <input
      className="mono"
      value={text}
      onChange={(e) => setText(e.target.value)}
      onBlur={() => text.trim() && text.trim() !== value && onSave(text.trim())}
      aria-label="How many per driver unit"
      style={{ width: 120 }}
    />
  );
}

/* --------------------------------------------------------------- suppliers */

function Suppliers({ canEdit }: { canEdit: boolean }) {
  const queryClient = useQueryClient();
  const [name, setName] = useState("");
  const [problem, setProblem] = useState<string | null>(null);
  const suppliers = useQuery({ queryKey: ["suppliers"], queryFn: () => api.catalogue.suppliers() });
  const add = useMutation({
    mutationFn: () => api.catalogue.addSupplier({ name: name.trim() }),
    onSuccess: () => {
      setName("");
      void queryClient.invalidateQueries({ queryKey: ["suppliers"] });
    },
    onError: (err) => setProblem(err instanceof ApiError ? err.message : "Couldn't add them."),
  });

  return (
    <Card title="Suppliers" subtitle="Who the parts come from — a purchase order is drafted per supplier">
      {problem && <p className="notice notice-crit">{problem}</p>}
      <div className="stack">
        {(suppliers.data ?? []).map((s) => (
          <div key={s.id} className="doc-row">
            <b>{s.name}</b>
            {s.account_code && <span className="tiny muted">{s.account_code}</span>}
          </div>
        ))}
        {suppliers.data && suppliers.data.length === 0 && <p className="tiny muted">None yet.</p>}
      </div>
      {canEdit && (
        <div className="btn-row" style={{ marginTop: 10 }}>
          <input value={name} onChange={(e) => setName(e.target.value)} placeholder="Supplier name" />
          <button className="btn" type="button" disabled={!name.trim() || add.isPending} onClick={() => add.mutate()}>
            Add
          </button>
        </div>
      )}
    </Card>
  );
}
